export const settingsModuleSlugs = [
  'general-settings',
  'tds-management',
  'charges',
  'test-samples',
  'tpa-management',
  'vials',
  'beds',
  'diagnoses',
  'packages',
  'document-headers',
  'users',
  'roles',
  'permissions',
  'doctors',
  'departments',
  'notices',
  'referral',
  'charge-categories',
  'charge-sections',
  'requisition-sections',
  'wards',
  'dialysis-machines',
  'ot-rooms',
  'procedure-types',
] as const;

export type SettingsModuleSlug = (typeof settingsModuleSlugs)[number];
export type SettingsStatus = 'active' | 'inactive';
export type SettingsLookupResource =
  | 'roles'
  | 'permissions'
  | 'vials'
  | 'charges'
  | 'test-samples'
  | 'charge-categories'
  | 'charge-sections'
  | 'requisition-sections'
  | 'wards'
  | 'departments';

export interface SettingsReference {
  id?: string;
  _id?: string;
  name?: string;
  label?: string;
  [key: string]: unknown;
}

export interface SettingsRecord {
  id: string;
  _id?: string;
  status?: SettingsStatus | 'archived';
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface SettingsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SettingsListResponse {
  success: true;
  data: SettingsRecord[];
  pagination: SettingsPagination;
}

export interface SettingsRecordResponse {
  success: true;
  data: SettingsRecord;
}

export interface GeneralSettingsResponse {
  success: true;
  data: SettingsRecord | null;
}

export type SettingsFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'multi-select'
  | 'checkbox-group'
  | 'email'
  | 'date'
  | 'image';

export type SettingsFormValue = string | boolean | string[];
export type SettingsFormValues = Record<string, SettingsFormValue>;

export interface SettingsFieldOption {
  value: string;
  label: string;
}

export interface SettingsFormField {
  path: string;
  label: string;
  type: SettingsFieldType;
  section: string;
  required?: boolean;
  help?: string;
  placeholder?: string;
  options?: readonly SettingsFieldOption[];
  lookupResource?: SettingsLookupResource;
  lookupValue?: 'id' | 'label';
  lookupSearchableSelect?: boolean;
  lookupParentPath?: string;
  lookupSearchable?: boolean;
  defaultValue?: SettingsFormValue;
  min?: number;
  max?: number;
  step?: number;
  fullWidth?: boolean;
  minimumValuePath?: string;
  accept?: string;
}

export type SettingsColumnFormat = 'text' | 'number' | 'currency' | 'date' | 'boolean' | 'list' | 'reference' | 'doctor-name';

export interface SettingsTableColumn {
  label: string;
  path: string;
  format?: SettingsColumnFormat;
  lookupResource?: SettingsLookupResource;
}

export type SettingsEditorKind = 'generic' | 'charge' | 'package' | 'document-header';

export interface SettingsModuleDefinition {
  slug: SettingsModuleSlug;
  label: string;
  singular: string;
  description: string;
  searchPlaceholder: string;
  columns: readonly SettingsTableColumn[];
  fields: readonly SettingsFormField[];
  singleton?: boolean;
  editor?: SettingsEditorKind;
  showView?: boolean;
}

export interface SettingsListParams {
  page: number;
  limit: number;
  search?: string;
  status?: SettingsStatus;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  category?: string;
  subCategory?: string;
}

export const tdsSections = [
  'DAYCARE',
  'DIALYSIS',
  'EMG',
  'INVESTIGATION',
  'IPD',
  'OPD',
  'PATHOLOGY',
] as const;

export type TdsSection = (typeof tdsSections)[number];

export interface TdsManagementRecord extends SettingsRecord {
  billNumber: string;
  section: TdsSection;
  insuranceType: string;
  paymentDate: string;
  paymentMode: string;
  paymentAmount: number;
  paymentTds: number;
  applied: boolean;
}

export interface TdsListResponse {
  success: true;
  data: TdsManagementRecord[];
  pagination: SettingsPagination;
}

export interface TdsApplyChange {
  id: string;
  applied: boolean;
}
